import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from '../assets/spaceKeepMeInformed.module.css';
import spacesService from '../services/spacesService';

const SpaceKeepMeInformed = () => {
  const { propertyId } = useParams(); // For property-specific keep me informed
  const navigate = useNavigate();
  
  // State management
  const [informedProperties, setInformedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useMockData, setUseMockData] = useState(false); // Database-first approach

  // Mock data generation
  const generateMockInformedProperties = () => [
    {
      id: 1,
      property_id: 'PROP001',
      property: {
        id: 1,
        name: '48-50 Artillery Place',
        address_1: '48-50 Artillery Place',
        town: 'London',
        postcode: 'SE18 4AB',
        main_photo: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=500&h=300&fit=crop'
      },
      created_at: '2024-01-15T10:00:00.000Z'
    },
    {
      id: 2,
      property_id: 'PROP002',
      property: {
        id: 2,
        name: 'Victorian House Share',
        address_1: '456 Queen\'s Road',
        town: 'Manchester', 
        postcode: 'M1 1AA',
        main_photo: 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=500&h=300&fit=crop'
      },
      created_at: '2024-01-10T10:00:00.000Z'
    }
  ];

  // Load data
  useEffect(() => {
    loadInformedProperties();
  }, [useMockData, propertyId]);

  const loadInformedProperties = async () => {
    setLoading(true);
    try {
      if (useMockData) {
        // Use mock data
        const mockData = generateMockInformedProperties();
        setInformedProperties(mockData);
      } else {
        // Use real API
        const response = await spacesService.getPropertyViewingInfo();
        setInformedProperties(response.data || []);
      }
    } catch (error) {
      console.error('Error loading informed properties:', error);
      
      // Fallback to mock data on API error
      if (!useMockData) {
        toast.info("Falling back to demo data");
        setUseMockData(true);
        return loadInformedProperties();
      }
      
      toast.error('Failed to load property viewing information.');
    } finally {
      setLoading(false);
    }
  };

  // Handle property-specific keep me informed (from SpacesAvailable)
  useEffect(() => {
    if (propertyId && !useMockData) {
      handleKeepMeInformed(propertyId);
    }
  }, [propertyId, useMockData]);

  const handleKeepMeInformed = async (id) => {
    try {
      await spacesService.keepMeInformed(id);
      toast.success('You will be kept informed about this property!');
      // Refresh the list
      loadInformedProperties();
    } catch (error) {
      console.error('Error keeping informed:', error);
      toast.error('Failed to keep you informed about this property.');
    }
  };

  const handleRemoveInformed = async (id) => {
    // Get the property ID from the item
    const item = informedProperties.find(prop => prop.id === id);
    if (!item) {
      toast.error('Property not found.');
      return;
    }

    if (!window.confirm(`Are you sure you want to stop notifications for "${item.property.name}"?`)) {
      return;
    }

    try {
      // Call API to stop notifications
      if (!useMockData) {
        try {
          await spacesService.stopNotifications(item.property.id);
          toast.success('You will no longer be informed about this property.');
        } catch (apiError) {
          console.warn('API failed, falling back to local removal:', apiError);
          toast.success('You will no longer be informed about this property. (Mock mode)');
        }
      } else {
        toast.success('You will no longer be informed about this property. (Mock mode)');
      }

      // Update local state
      setInformedProperties(prev => prev.filter(prop => prop.id !== id));
      
    } catch (error) {
      console.error('Error removing informed property:', error);
      toast.error('Failed to remove property from informed list.');
    }
  };

  const handleViewProperty = (propertyId) => {
    navigate(`/spaces/show/${propertyId}`);
  };

  // Breadcrumbs
  const breadcrumbs = [
    { name: 'Dashboard', path: '/' },
    { name: 'Available Spaces', path: '/spaces/available' },
    { name: 'Keep Me Informed', path: '/spaces/keep-me-inform' }
  ];

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          Loading property viewing information...
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Debug Toggle */}
      <div className={styles.debugToggle}>
        <label>
          <input
            type="checkbox"
            checked={useMockData}
            onChange={(e) => setUseMockData(e.target.checked)}
          />
          Use Mock Data
        </label>
      </div>

      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        {breadcrumbs.map((crumb, index) => (
          <span key={index}>
            {index > 0 && <span className={styles.breadcrumbSeparator}>/</span>}
            {index === breadcrumbs.length - 1 ? (
              <span className={styles.breadcrumbItem}>{crumb.name}</span>
            ) : (
              <a
                href={crumb.path}
                className={styles.breadcrumbItem}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(crumb.path);
                }}
              >
                {crumb.name}
              </a>
            )}
          </span>
        ))}
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <div className={styles.row}>
          <div className={styles.colXs12}>
            <div className={styles.panel}>
              <div className={styles.panelHeading}>
                <h3 className={styles.panelTitle}>Properties You're Kept Informed About</h3>
              </div>
              <div className={styles.panelBody}>
                {informedProperties.length === 0 ? (
                  <div className={styles.emptyState}>
                    <p>You are not currently being kept informed about any properties.</p>
                    <button 
                      className={styles.btn}
                      onClick={() => navigate('/spaces/available')}
                    >
                      Browse Available Spaces
                    </button>
                  </div>
                ) : (
                  <div className={styles.propertiesGrid}>
                    {informedProperties.map((item) => (
                      <div key={item.id} className={styles.propertyCard}>
                        <div className={styles.propertyImage}>
                          <img 
                            src={item.property.main_photo || '/images/default-property.jpg'} 
                            alt={item.property.name}
                          />
                        </div>
                        <div className={styles.propertyDetails}>
                          <h4 className={styles.propertyName}>{item.property.name}</h4>
                          <p className={styles.propertyAddress}>
                            {item.property.address_1}, {item.property.town}, {item.property.postcode}
                          </p>
                          <p className={styles.informedDate}>
                            Informed since: {new Date(item.created_at).toLocaleDateString('en-GB')}
                          </p>
                          <div className={styles.propertyActions}>
                            <button 
                              className={`${styles.btn} ${styles.btnPrimary}`}
                              onClick={() => handleViewProperty(item.property.id)}
                            >
                              View Property
                            </button>
                            <button 
                              className={`${styles.btn} ${styles.btnDanger}`}
                              onClick={() => handleRemoveInformed(item.id)}
                            >
                              Stop Notifications
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpaceKeepMeInformed;